import { SHOCKABLE_WRAPPERS } from './schemas-household.js';
import { isPresetReviewed, unsourcedFigures } from './historical-presets.js';
/**
 * Which wrappers a historic period reaches, and why the rest are left alone.
 *
 * Stated here rather than inferred, because every line of it is a claim about
 * what a public index does and does not describe:
 *
 * - the investable wrappers hold a mix of equity and bonds, and the adviser
 *   says what that mix is;
 * - property takes the house-price series directly — it IS one of the register's
 *   asset classes, so no mix is involved and none is asked for;
 * - cash-like wrappers are untouched. The register is explicit that cash is not
 *   a shockable column (brief C2.4), and a cash ISA that fell 30% in 2008 would
 *   be a straightforwardly false picture of 2008;
 * - a business and "other" are not a public asset class at all. Applying an
 *   equity index to a client's own company is a guess wearing a citation, and
 *   the projection says so rather than making it.
 */
export const WRAPPER_TREATMENT = Object.freeze({
    stocks_isa: { kind: 'blend' },
    lisa: { kind: 'blend' },
    jisa: { kind: 'blend' },
    gia: { kind: 'blend' },
    dc_pension: { kind: 'blend' },
    property: { kind: 'class', assetClass: 'property' },
    cash_isa: { kind: 'untouched', because: 'cash is not shocked by these datasets' },
    nsi: { kind: 'untouched', because: 'cash is not shocked by these datasets' },
    business: {
        kind: 'untouched',
        because: 'a private business is not covered by any of these public series',
    },
    other_asset: {
        kind: 'untouched',
        because: 'not covered by any of these public series',
    },
});
/** The wrappers a historic period actually moves, for the UI to name up front. */
export const HISTORICALLY_SHOCKED_WRAPPERS = SHOCKABLE_WRAPPERS.filter((w) => WRAPPER_TREATMENT[w]?.kind !== 'untouched');
/** The wrappers it deliberately does not, with the reason, for the same purpose. */
export const HISTORICALLY_UNTOUCHED_WRAPPERS = SHOCKABLE_WRAPPERS.flatMap((w) => {
    const t = WRAPPER_TREATMENT[w];
    return t !== undefined && t.kind === 'untouched' ? [{ wrapper: w, because: t.because }] : [];
});
export class HistoricalPresetRefused extends Error {
}
const BPS = 10_000;
const clampDelta = (v) => Math.max(-BPS, Math.min(BPS, Math.round(v)));
/**
 * The preset's return for one wrapper in one year, in basis points, or
 * `undefined` where the wrapper is deliberately left alone.
 */
function returnForWrapper(wrapper, byClass, equityShareBps) {
    const treatment = WRAPPER_TREATMENT[wrapper];
    if (treatment === undefined || treatment.kind === 'untouched')
        return undefined;
    if (treatment.kind === 'class') {
        const v = byClass[treatment.assetClass];
        if (v === null)
            throw new HistoricalPresetRefused(`${treatment.assetClass} figure is not sourced`);
        return v;
    }
    const equity = byClass.equity;
    const bonds = byClass.bonds;
    if (equity === null)
        throw new HistoricalPresetRefused('equity figure is not sourced');
    if (bonds === null)
        throw new HistoricalPresetRefused('bonds figure is not sourced');
    const w = equityShareBps / BPS;
    return equity * w + bonds * (1 - w);
}
/**
 * A reviewed preset as a market shock on this plan.
 *
 * Throws `HistoricalPresetRefused` rather than returning a degraded shock: a
 * stress test that quietly modelled less than it claimed would be worse than no
 * stress test, because the adviser would present it as the period it names.
 */
export function historicalPresetShock(preset, opts) {
    if (!preset.offered || !isPresetReviewed(preset)) {
        throw new HistoricalPresetRefused(`"${preset.name}" has not been reviewed and signed for version ${preset.version}, so it cannot be applied to a plan.`);
    }
    const unsourced = unsourcedFigures(preset);
    if (unsourced.length > 0) {
        throw new HistoricalPresetRefused(`"${preset.name}" still has unsourced figures (${unsourced.slice(0, 4).join(', ')}${unsourced.length > 4 ? ', …' : ''}), so it cannot be applied to a plan.`);
    }
    if (!Number.isInteger(opts.equityShareBps) ||
        opts.equityShareBps < 0 ||
        opts.equityShareBps > BPS) {
        throw new HistoricalPresetRefused('The equity share must be a whole number of basis points between 0 and 10000.');
    }
    const first = preset.years[0]?.year;
    if (first === undefined)
        throw new HistoricalPresetRefused('The preset has no years.');
    const path = preset.years.map((y) => {
        const deltas = {};
        for (const wrapper of SHOCKABLE_WRAPPERS) {
            const total = returnForWrapper(wrapper, y.totalReturnBpsByAssetClass, opts.equityShareBps);
            if (total === undefined)
                continue;
            const base = opts.baseGrowthBpsByWrapper[wrapper] ?? 0;
            deltas[wrapper] = clampDelta(total - base);
        }
        return {
            // The register is a run of consecutive calendar years, so the offset is
            // the year's distance from the first — never the array index, which would
            // silently close a gap if a period ever skipped a year.
            offsetYears: y.year - first,
            returnDeltaBpsByWrapper: deltas,
        };
    });
    return {
        id: opts.id,
        /**
         * The register's client-facing name, verbatim, and NOTHING added. It
         * already carries the neutral pattern-label form owner decision N-C2 fixed
         * ("Markets fall about 30%, then rise (shaped on 2008–2012)"), and the
         * banned-words guard is written against it. Appending anything here — the
         * word "stress", a percentage, the client's name — would put wording on a
         * client-facing label that no reviewer signed.
         */
        label: preset.name,
        anchor: { year: opts.anchorYear },
        path,
    };
}
/**
 * What the adviser is told the stress DID, in one factual sentence, for the
 * assumptions snapshot and the scenario editor.
 *
 * Deliberately not a verdict and not a summary of the outcome — the register's
 * banned-word list exists because a sentence about a stress test is the easiest
 * place in this product to write advice by accident.
 */
export function historicalPresetApplicationNote(preset, opts) {
    const share = opts.equityShareBps / 100;
    const last = opts.anchorYear + (preset.years.length - 1);
    return (`${preset.name}, applied from ${opts.anchorYear} to ${last} ` +
        `(dataset ${preset.id}@${preset.version}). ` +
        `Investment wrappers are treated as ${share}% equity and ${100 - share}% bonds; ` +
        `property follows the house-price series; ` +
        `cash, a business and other assets are not moved by it.`);
}
//# sourceMappingURL=apply-historical-preset.js.map